import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dateFormat from 'dateformat';
import { Row, Col, Collapse, Button, Table } from 'reactstrap';
import { noop, get } from 'lodash-es';

// Utils
import { getAccessiblePrice } from 'site-modules/shared/utils/a11y-utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import {
  VALIDATORS,
  sendEmail,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { numberWithCommasForStringV2 } from 'site-modules/shared/utils/string';

// Constants
import {
  PRICE_INFO,
  TRADE_IN_VALUE,
  TRANSACTION_INFO,
  VIEW_APPRAISAL_DROPDOWN_SELECT_TRACKING,
  CONDITIONS_FOR_TABLE_DISPLAY,
  APPRAISER_REPORT_CREATIVE_ID,
  APPRAISAL_REPORT_TYPE_TRACKING,
  INSTANT_OFFER,
  COLORS_AND_OPTIONS,
  LOCATION_AND_STYLE,
  EMAIL_REPORT_STATUSES,
  APPRAISAL_TRACK_CAR_CREATIVE_ID,
  TRANSACTION_TYPE_BUTTONS_OPTIONS,
  TRANSACTION_TYPE_BUTTON_IDS,
  TRANSACTION_TYPE_PANEL_IDS,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { TrackingConstant, PAGE_CONTENT_UPDATE } from 'client/tracking/constant';

// Components
import { TransactionTypeSelect } from 'site-modules/shared/components/appraisal/transaction-type-select/transaction-type-select';
import { TransactionTypeSelectButtons } from 'site-modules/shared/components/appraisal/transaction-type-select-buttons/transaction-type-select-buttons';
import { AppraisalReportStepPriceTitle } from 'site-modules/shared/components/appraisal/appraisal-report-price-title/appraisal-report-price-title';
import { AppraisalReportConditionDropdown } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step/appraisal-report-step-condition-dropdown';
import { AppraisalReportStepBarChart } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step/appraisal-report-step-bar-chart';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { TcvPageCta } from 'site-modules/shared/components/appraisal/tcv-page-cta/tcv-page-cta';
import { AppraisalReportStepExtendedEmailSection } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step-extended/appraisal-report-step-extended-email-section';
import { AppraisalReportDisclaimer } from 'site-modules/shared/components/appraisal/appraisal-report-disclaimer/appraisal-report-disclaimer';

import './appraisal-report-extended-details.scss';

const SELECTED_OUTLINED_CLASS = 'selected-outlined';

/**
 * AppraisalReportExtendedDetailed component.
 */
export class AppraisalReportExtendedDetailsUI extends Component {
  static propTypes = {
    stepId: PropTypes.string.isRequired,
    isDetailedReportOpen: PropTypes.bool.isRequired,
    changeStep: PropTypes.func.isRequired,
    emailReportStatus: PropTypes.string,
    resetEmailReportStatus: PropTypes.func,
    updateStepsState: PropTypes.func,
    allConditionPricing: PropTypes.shape({
      rough: PropTypes.object,
      average: PropTypes.object,
      clean: PropTypes.object,
      outstanding: PropTypes.object,
    }).isRequired,
    selectedReportType: PropTypes.string,
    condition: PropTypes.string,
    creativeId: PropTypes.string,
    getVehicleData: PropTypes.func.isRequired,
    mileage: PropTypes.string,
    optionIds: PropTypes.arrayOf(PropTypes.string),
    vin: PropTypes.string,
    color: PropTypes.shape({}),
    showVehicleDetails: PropTypes.bool,
    showBarChart: PropTypes.bool,
    showExteriorColor: PropTypes.bool,
    showAddEquipment: PropTypes.bool,
    zipCode: PropTypes.string,
    validationErrors: PropTypes.objectOf(PropTypes.string),
    validate: PropTypes.func.isRequired,
    userEmail: PropTypes.string,
    fields: PropTypes.shape({}),
    updateIdmData: PropTypes.func.isRequired,
    modsId: PropTypes.string,
    vehicle: PropTypes.shape({}),
    isMobile: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
    doesNotHaveEmail: PropTypes.bool,
    fieldRef: PropTypes.func,
  };

  static defaultProps = {
    emailReportStatus: '',
    resetEmailReportStatus: noop,
    updateStepsState: noop,
    condition: '',
    selectedReportType: TRADE_IN_VALUE,
    creativeId: APPRAISER_REPORT_CREATIVE_ID,
    mileage: '',
    optionIds: [],
    color: {},
    vin: '',
    showVehicleDetails: false,
    showBarChart: true,
    showExteriorColor: false,
    showAddEquipment: false,
    zipCode: '',
    validationErrors: {},
    userEmail: '',
    fields: {},
    modsId: '',
    vehicle: {},
    isMobile: false,
    isAuthenticated: false,
    doesNotHaveEmail: false,
    fieldRef: noop,
  };

  static getDerivedStateFromProps(nextProps, currentState) {
    const { emailReportStatus, selectedReportType } = nextProps;
    const { isSubmitting, isSubmitted } = currentState;
    const stateUpdate = {};
    if (selectedReportType && selectedReportType !== currentState.selectedType) {
      stateUpdate.selectedType = selectedReportType;
    }

    if (emailReportStatus === EMAIL_REPORT_STATUSES.SENT) {
      if (isSubmitting) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
          event_data: {
            action_name: TrackingConstant.ACTION_SIGN_UP,
            subaction_name: TrackingConstant.SUCCESS_EMAIL_ONLY_SIGN_UP,
            action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            creative_id: APPRAISAL_TRACK_CAR_CREATIVE_ID,
          },
        });
      }
      stateUpdate.isSubmitting = false;
      stateUpdate.isSubmitted = isSubmitted || isSubmitting;
    }

    return stateUpdate;
  }

  state = {
    selectedType: this.props.selectedReportType,
    isSubmitting: false,
    isSubmitted: false,
    inViewPort: false,
  };

  componentDidMount() {
    EventToolbox.fireCustomEvent(PAGE_CONTENT_UPDATE);
  }

  onEmailFormFieldFocus = () => {
    const { emailReportStatus, resetEmailReportStatus } = this.props;
    if (this.state.isSubmitted) {
      if (emailReportStatus === EMAIL_REPORT_STATUSES.SENT) {
        resetEmailReportStatus();
      }
      this.setState({ isSubmitted: false });
    }
  };

  getPrices = (priceLabel, isOptionValues = false) => {
    const { allConditionPricing } = this.props;
    const { selectedType } = this.state;
    return isOptionValues
      ? allConditionPricing.rough[priceLabel]
      : CONDITIONS_FOR_TABLE_DISPLAY.map(
          ({ title }) => allConditionPricing[title.toLowerCase()][priceLabel][selectedType]
        );
  };

  handleTypeChange = ({ value }) => {
    const { stepId } = this.props;
    this.setState(() => ({ selectedType: value }));
    this.props.updateStepsState({ selectedReportType: value });
    EventToolbox.fireTrackAction(VIEW_APPRAISAL_DROPDOWN_SELECT_TRACKING(value, this.props.creativeId));
    EventToolbox.fireTrackAction(APPRAISAL_REPORT_TYPE_TRACKING[stepId][value]);
  };

  handleConditionChange = updatedCondition => {
    this.props.updateStepsState({ condition: updatedCondition });
  };

  handleSubmitEmail = async e => {
    const { validate, resetEmailReportStatus, fields } = this.props;
    e.preventDefault();
    e.stopPropagation();
    const isValid = validate();
    if (isValid) {
      resetEmailReportStatus();
    } else {
      fields?.email?.focus?.();
    }
    const emailValue = isValid ? fields.email?.value() : undefined;
    this.setState({ isSubmitting: isValid }, async () => {
      const { isAuthenticated, doesNotHaveEmail, updateIdmData } = this.props;
      await sendEmail({
        isAuthenticated,
        doesNotHaveEmail,
        isValid,
        email: emailValue,
        updateIdmData,
      });
    });
  };

  renderPriceRow = ({
    prices,
    className,
    priceClassName = '',
    titleProps,
    conditionOrderNumber,
    titleContainerClassName,
  }) => {
    if (!prices.find(i => i !== undefined)) return null; // If all condition prices are undefined, return null
    return (
      <tr
        data-test-id="extended-report-table-row"
        key={titleProps.title}
        className={classNames('extended-report-table-row px-0_25 row', className)}
      >
        <th scope="row" className={classNames('col-8 col-lg-4 border-0 px-0_5', titleContainerClassName)}>
          <AppraisalReportStepPriceTitle iconClassName="small text-gray-darker" {...titleProps} />
        </th>
        {prices.map((price, index) => {
          const key = `${price}-${titleProps.id}-${index}`;
          const isSelected = index === conditionOrderNumber;
          return (
            <td
              key={key}
              className={classNames(
                'd-flex justify-content-end py-0_5 py-lg-0_75 hidden-lg-down col-2',
                priceClassName,
                {
                  [SELECTED_OUTLINED_CLASS]: isSelected,
                  'border-0': !isSelected,
                }
              )}
            >
              {getAccessiblePrice(price)}
            </td>
          );
        })}
        <td className={classNames('text-end py-0_5 py-lg-0_75 hidden-lg-up border-0 col-4', priceClassName)}>
          {getAccessiblePrice(prices[conditionOrderNumber])}
        </td>
      </tr>
    );
  };

  renderReportDetailHeader = () => {
    const { stepId } = this.props;
    return (
      <div className="text-center">
        <h3 className="heading-3 d-flex flex-column flex-lg-row align-items-center justify-content-center mb-0">
          <span className="icon-edmunds-car text-primary size-30 me-0_5" aria-hidden />
          Your Edmunds Appraisal <span className="d-none d-md-block">&nbsp;Report</span>
        </h3>
        <div className="my-0_5 size-14">As of {dateFormat(Date.now(), 'mm/dd/yyyy')}</div>
        {stepId === INSTANT_OFFER && <AppraisalReportDisclaimer className="my-0_5 mb-lg-1_5 size-14" />}
      </div>
    );
  };

  renderReportTableHeader = () => {
    const {
      changeStep,
      isMobile,
      getVehicleData,
      mileage,
      color,
      vin,
      stepId,
      showBarChart,
      showVehicleDetails,
      zipCode,
      showExteriorColor,
      showAddEquipment,
      condition,
      validationErrors,
      validate,
      userEmail,
      fieldRef,
    } = this.props;
    const { selectedType, isSubmitting, isSubmitted } = this.state;
    const { makeName, modelName, styleName, year } = getVehicleData();
    return (
      <div className="text-gray-darker">
        {showVehicleDetails ? (
          <Fragment>
            <div>{this.renderReportDetailHeader()}</div>
            <div className="appraisal-report-vehicle-details size-16 text-gray-darker text-center mt-1 mb-1_5">
              <div className="fw-bold">
                {year} {makeName} {modelName}
              </div>
              <div>{styleName}</div>
              <div className="appraisal-report-vehicle-details-container d-flex flex-column flex-lg-row justify-content-around align-items-center mt-0_75 size-14">
                {stepId === INSTANT_OFFER && (
                  <div>
                    VIN: <span className="fw-medium">{vin}</span>
                  </div>
                )}
                {showExteriorColor && (
                  <div>
                    Exterior: <span className="fw-medium">{color.name}</span>
                  </div>
                )}
                <div className="d-flex">
                  <div className="me-2">
                    Mileage: <span className="fw-medium">{numberWithCommasForStringV2(mileage)}</span>
                  </div>
                  <div>
                    Zip code: <span className="fw-medium">{zipCode}</span>
                  </div>
                </div>
              </div>
              <Button
                color="link"
                onClick={changeStep}
                className="d-flex justify-content-center align-items-center size-12 p-0 mx-auto mt-0_5 mb-0 mt-0_5 text-transform-none text-cool-gray-30 fw-normal"
                data-tracking-id="edit_vehicle"
                data-index={LOCATION_AND_STYLE}
              >
                <i className="icon-edit2 me-0_25 size-16" aria-hidden /> Edit vehicle
                <span className="visually-hidden">This will take you back to Step 1</span>
              </Button>
              {showAddEquipment && (
                <Button
                  onClick={changeStep}
                  className="border-0 p-0 text-transform-none fw-normal text-primary-darker btn-link text-start edit-link"
                  data-tracking-id="edit_options"
                  data-index={COLORS_AND_OPTIONS}
                >
                  <span className="icon-plus-circle2 me-0_5 text-primary-darker" aria-hidden />
                  Add additional equipment to my appraisal
                </Button>
              )}
            </div>
          </Fragment>
        ) : (
          <div>{this.renderReportDetailHeader()}</div>
        )}
        <AppraisalReportStepExtendedEmailSection
          validationErrors={validationErrors}
          validate={validate}
          userEmail={userEmail}
          isMobile={isMobile}
          isSubmitting={isSubmitting}
          isSubmitted={isSubmitted}
          fieldRef={fieldRef}
          handleSubmitEmail={this.handleSubmitEmail}
          onEmailFormFieldFocus={this.onEmailFormFieldFocus}
        />
        <Row>
          <Col xs={12} className="mb-1">
            <TransactionTypeSelect
              labelText="Transaction type select"
              isLabelHidden
              onChange={this.handleTypeChange}
              selected={selectedType}
              className="d-block d-sm-none"
            />
            <TransactionTypeSelectButtons
              onChange={this.handleTypeChange}
              selected={selectedType}
              className="d-none d-sm-block"
            />
          </Col>
        </Row>
        {showBarChart &&
          TRANSACTION_TYPE_BUTTONS_OPTIONS.map(
            (option, index) =>
              option.value === selectedType && (
                <div
                  key={`panel-${option.value}`}
                  id={TRANSACTION_TYPE_PANEL_IDS[index]}
                  role="tabpanel"
                  aria-labelledby={TRANSACTION_TYPE_BUTTON_IDS[index]}
                >
                  <div className="size-12 text-center">{TRANSACTION_INFO[option.value].info}</div>
                  <div className="pb-2_5 pb-sm-1_5">
                    <AppraisalReportStepBarChart
                      prices={this.getPrices('totalPrices')}
                      selectedType={selectedType}
                      selectedCondition={condition}
                    />
                  </div>
                </div>
              )
          )}
      </div>
    );
  };

  renderYourAppraisal = className => (
    <div className={classNames('text-cool-gray-10 size-12 text-center fw-bold', className)}>Your car&apos;s value</div>
  );

  renderReportTable = () => {
    const { condition } = this.props;
    const { selectedType } = this.state;

    const allConditionPriceLists = [
      { ...PRICE_INFO.NATIONAL_BASE, prices: this.getPrices('nationalPrices') },
      {
        ...PRICE_INFO.OPTIONAL_EQUIPMENT,
        prices: this.getPrices('optionalPrices'),
        optionsValues: this.getPrices('optionalPricesValues', true),
      },
      { ...PRICE_INFO.COLOR_ADJUSTMENT, prices: this.getPrices('colorPrices') },
      { ...PRICE_INFO.REGIONAL_ADJUSTMENT, prices: this.getPrices('regionalPrices') },
      { ...PRICE_INFO.MILEAGE_ADJUSTMENT, prices: this.getPrices('mileagePrices') },
      { ...PRICE_INFO.CONDITION_ADJUSTMENT, prices: this.getPrices('conditionPrices') },
      { title: 'Value', prices: this.getPrices('totalPrices'), isTotal: true },
    ];

    const tableHeadConditionSelected = (
      <tr className="extended-report-table-head-your-appraisal text-cool-gray-10 px-0_25 hidden-lg-down row">
        <td className="col-7 col-lg-4 border-0 p-0" />

        {CONDITIONS_FOR_TABLE_DISPLAY.map(({ title }) => {
          const isSelected = title === condition;
          const key = `table-head-condition-selected-${title}`;

          return isSelected ? (
            <th key={key} className={`py-0_5 col-2 px-0 ${SELECTED_OUTLINED_CLASS}`} scope="col">
              {this.renderYourAppraisal()}
            </th>
          ) : (
            <td key={key} className="py-0_5 col-2 px-0 border-0" />
          );
        })}
      </tr>
    );

    const commonPaddingYForTableHead = 'py-1 py-lg-0_5';

    const tableHead = (
      <tr className="extended-report-table-head px-0_25 size-14 fw-bold row">
        <th
          className={classNames(
            'd-flex align-items-end align-items-lg-baseline border-0 col-7 col-lg-4',
            commonPaddingYForTableHead
          )}
        >
          <span className="visually-hidden">Price categories</span>
        </th>
        {CONDITIONS_FOR_TABLE_DISPLAY.map(({ title, text }) => (
          <th
            key={title}
            className={classNames(
              'text-end hidden-lg-down col-2 d-flex justify-content-end pe-lg-0 ps-lg-0',
              commonPaddingYForTableHead,
              title === condition ? `${SELECTED_OUTLINED_CLASS} text-cool-gray-10` : 'border-0 text-gray-darker'
            )}
            scope="col"
          >
            <div className="d-none d-lg-block d-xl-inline">{title}</div>
            <PopoverItem
              className="appraisal-table-info-btn d-flex text-start"
              iconClassName="small"
              placement="bottom"
              screenReaderTooltipLabel={`${title} condition information`}
            >
              {text}
            </PopoverItem>
          </th>
        ))}
        <th
          className={classNames(
            'hidden-lg-up text-end d-flex align-items-end justify-content-end border-0 col-5',
            commonPaddingYForTableHead
          )}
          scope="col"
        >
          <div>
            {this.renderYourAppraisal('pe-1_25')}
            <AppraisalReportConditionDropdown onUpdate={this.handleConditionChange} selectedCondition={condition} />
          </div>
        </th>
      </tr>
    );

    const tableBody = allConditionPriceLists.map(list => {
      const { title, info, prices, optionsValues = [], totalPrice, isTotal } = list;
      const conditionOrderNumber = CONDITIONS_FOR_TABLE_DISPLAY.findIndex(cnd => cnd.title === condition);

      const options =
        !!optionsValues.length &&
        optionsValues.map(({ name, price }) =>
          this.renderPriceRow({
            prices: Array(CONDITIONS_FOR_TABLE_DISPLAY.length).fill(price[selectedType]),
            conditionOrderNumber,
            className: 'extended-report-table-option-row',
            titleProps: {
              title: name,
              id: makeNiceName(name),
            },
            titleContainerClassName: 'py-0_5 py-lg-0_75 ps-1 fw-normal',
          })
        );

      return (
        <Fragment key={title}>
          {this.renderPriceRow({
            prices,
            conditionOrderNumber,
            totalPrice,
            className: classNames('extended-report-table-item-row', {
              'extended-report-table-total-row': isTotal,
            }),
            priceClassName: { 'heading-4': isTotal },
            titleProps: {
              title: isTotal ? (
                <Fragment>
                  <span className="hidden-lg-up">{condition} Condition </span>
                  {title}
                </Fragment>
              ) : (
                title
              ),
              info,
              id: makeNiceName(title),
            },
            titleContainerClassName: classNames('py-lg-0_75', {
              'py-0_75 fw-bold': isTotal,
              'py-0_5 fw-normal': !isTotal,
            }),
          })}
          {options}
        </Fragment>
      );
    });

    return (
      <Table className="text-gray-darker size-14">
        <thead>
          {tableHeadConditionSelected}
          {tableHead}
        </thead>
        <tbody>{tableBody}</tbody>
      </Table>
    );
  };

  renderTcvPageCta = () => {
    const { getVehicleData, mileage, optionIds, zipCode, condition, modsId } = this.props;

    return (
      <Fragment>
        <hr className="mt-1_5 mb-3 d-none d-lg-block" />
        <TcvPageCta
          getVehicleData={getVehicleData}
          mileage={mileage}
          zipCode={zipCode}
          optionIds={optionIds}
          condition={condition}
          className="mt-2_5 mt-md-1_5 mb-3 px-0_75"
          modsId={modsId}
        />
      </Fragment>
    );
  };

  render() {
    const { isDetailedReportOpen } = this.props;

    return (
      <Fragment>
        {this.renderReportTableHeader()}
        <Collapse isOpen={isDetailedReportOpen}>
          {this.renderReportTable()}
          {this.renderTcvPageCta()}
        </Collapse>
      </Fragment>
    );
  }
}

export const AppraisalReportExtendedDetailsWithIdmWrapper = insiderMethods(AppraisalReportExtendedDetailsUI);

export const mapStateToProps = state => ({
  userEmail: get(state, 'profile.data.idm.identifiers.email[0]'),
  isAuthenticated: state.profile.isAuthenticated,
  doesNotHaveEmail: !get(state, 'profile.data.idm.identifiers.email[0]'),
});

export const AppraisalReportExtendedDetails = connect(mapStateToProps)(
  formValidation(AppraisalReportExtendedDetailsWithIdmWrapper, VALIDATORS)
);
